import React, { useState, useEffect } from 'react';
import { X, Crown, Rocket, CheckCircle, Clock, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * PremiumExpiredModal - Shows a modal when user's org-granted premium access has expired
 * Only shows once per session (or until dismissed)
 */
const PremiumExpiredModal = ({ user }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  
  useEffect(() => {
    // Check if user has expired org premium access
    const orgPremiumAccess = user?.org_premium_access;
    
    if (!orgPremiumAccess?.enabled) {
      return;
    }
    
    const expiresAt = orgPremiumAccess.expires_at ? new Date(orgPremiumAccess.expires_at) : null;
    const now = new Date();
    const hasExpired = expiresAt && expiresAt < now;
    
    if (!hasExpired) {
      return;
    }
    
    // Check if we've already shown the modal this session
    const sessionKey = `premium_expired_modal_shown_${user?.id}`;
    const alreadyShown = sessionStorage.getItem(sessionKey);
    
    if (!alreadyShown) {
      // Show modal after a short delay
      const timer = setTimeout(() => {
        setOpen(true);
        sessionStorage.setItem(sessionKey, 'true');
      }, 1500);
      
      return () => clearTimeout(timer);
    }
  }, [user]);
  
  const orgName = user?.org_premium_access?.org_name || 'your organization';
  
  const handleUpgrade = () => {
    setOpen(false);
    navigate('/pricing');
  };
  
  const handleDismiss = () => {
    setOpen(false);
  };
  
  const premiumFeatures = [
    { icon: '🚀', text: 'WFH Remote Jobs Directory' },
    { icon: '📄', text: '18+ Premium Resume Templates' },
    { icon: '💼', text: 'LinkedIn Profile Optimizer' },
    { icon: '🎓', text: 'GED Student Tools' },
    { icon: '👥', text: 'Community Forum Access' },
    { icon: '⚡', text: 'Priority Support' },
  ];
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-6 py-8 text-center relative">
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 p-1 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <X className="h-4 w-4 text-white" />
          </button>
          
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 mb-4">
            <Crown className="h-8 w-8 text-white" />
          </div>
          
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white">
              Your Premium Access Has Expired
            </DialogTitle>
          </DialogHeader>
          
          <p className="text-gray-300 mt-2 text-sm">
            Your access through <strong className="text-white">{orgName}</strong> has ended
          </p>
        </div>
        
        {/* Content */}
        <div className="px-6 py-6">
          <p className="text-gray-600 text-center mb-6">
            Don&apos;t lose access to these premium features:
          </p>
          
          <div className="grid grid-cols-2 gap-3 mb-6">
            {premiumFeatures.map((feature, index) => (
              <div
                key={index}
                className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg"
              >
                <span className="text-lg">{feature.icon}</span>
                <span className="text-sm text-gray-700">{feature.text}</span>
              </div>
            ))}
          </div>
          
          {/* Urgency message */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-center">
            <div className="flex items-center justify-center gap-2 text-amber-700 font-medium">
              <Clock className="h-4 w-4" />
              <span>Limited Time: Get 20% off annual plans</span>
            </div>
          </div>
          
          {/* CTA Buttons */}
          <div className="flex flex-col gap-3">
            <Button
              onClick={handleUpgrade}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold py-6 text-lg"
            >
              <Rocket className="h-5 w-5 mr-2" />
              Upgrade Now
            </Button>
            
            <Button
              variant="ghost"
              onClick={handleDismiss}
              className="w-full text-gray-500 hover:text-gray-700"
            >
              Maybe Later
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PremiumExpiredModal;
